<template>
  <i
    class="tm-overlay-resize"
    :class="{
      l: $props.fromLeft,
      r: $props.fromRight,
      n: isNarrow,
      v: $props.visible || isActive,
      a: isActive,
    }"
    :style="styleObj"
    @mouseenter="$_onHoverOverlay"
    @mouseleave="$_onHoverOverlay"
    v-tm-move="moveObj"
  >
    <i class="-backlight" />
    <i class="-mark" />
  </i>
</template>

<script>
import {
  getResizeAvailability,
  mapAvailability2Cursor,
} from 'v/Story/Timeline/api/tapi';
import resizeVisualOverlayMixin from 'v/Story/Timeline/view/resizable/resizeVisualOverlay';
import tmMove from 'v/Story/Timeline/view/movable/tmMove';
import { mapState } from 'vuex';

export default {
  name: 'tm-overlay-resize',
  mixins: [resizeVisualOverlayMixin],
  directives: { tmMove },

  props: {
    visible: { type: Boolean, required: true },
    fromLeft: { type: Boolean, required: false, default: false },
    fromRight: { type: Boolean, required: false, default: false },
    rectangle: { type: Object, required: false, default: null },
    overlay: { type: Object, required: true },
    pipa: { type: Object, required: true },
    isOverlayResizing: { type: Boolean, required: true },
  },

  data() {
    return {
      hovered: false,
    };
  },

  computed: {
    ...mapState('timemachine', {
      pxResizeW: (state) => state.cssVariables.pxResizeW,
    }),

    isActive() {
      return this.$data.hovered || this.$props.isOverlayResizing;
    },

    isNarrow() {
      return this.$props.rectangle
        ? this.$props.rectangle.width <= 2 * this.pxResizeW
        : false;
    },

    styleObj() {
      let cursor = null;
      if (this.isActive) {
        // checking for cursor only if isActive to minimize load while rendering bunch of assets
        const resizeWindow = this.$props.pipa.getOverlayResizeTimeWindow(
          this.$props.overlay,
          { relativeToScene: true }
        );
        const availability = getResizeAvailability(
          resizeWindow,
          this.$props.overlay
        );
        const cursorMap = mapAvailability2Cursor(availability);
        cursor = this.$props.fromLeft ? cursorMap.left : cursorMap.right;
      }

      return this.$props.rectangle
        ? {
            transform: `translate3d(${
              this.$props.fromLeft
                ? this.$props.rectangle.p1.x
                : this.$props.rectangle.p2.x
            }px, ${this.$props.rectangle.p1.y}px, 0)`,
            ...(cursor ? { cursor } : null),
          }
        : null;
    },
  },

  methods: {
    $_onHoverOverlay(e) {
      this.$data.hovered = e.type === 'mouseenter';
      this.$emit('hoveredOverlay', this.$data.hovered, this.$props.rectangle);
    },
  },
};
</script>

<style lang="scss">
.tm-overlay > .tm-overlay-resize {
  --mark-colour: #fff;
  --backlight-visibility: hidden;

  position: absolute;
  top: var(--tm-pxOverlayTopOffset);
  z-index: var(--tm-zOverlayResize);
  width: var(--tm-pxResizeW);
  height: var(--tm-pxOverlayH);
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s linear;

  &.v {
    visibility: visible;
    opacity: 1;
  }
  &.a {
    --mark-colour: #{$color-secondary};
    --backlight-visibility: visible;
  }

  > .-backlight {
    position: absolute;
    visibility: var(--backlight-visibility);
  }

  > .-mark {
    position: absolute;
    width: 5px;
    height: 20px;
    top: 50%;
    transform: translateY(-50%);
    border-right: 2px solid var(--mark-colour);
    border-left: 2px solid var(--mark-colour);
    box-shadow: 0 2px 4px $color-black-l;
  }

  &.l {
    left: 0;
    > .-backlight {
      top: var(--tm-pxVisualSpanBorder);
      right: 0;
      bottom: var(--tm-pxVisualSpanBorder);
      left: var(--tm-pxVisualSpanBorder);
      background-image: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      );
    }
    > .-mark {
      left: 8px;
    }
    &.n {
      left: -8px;
      width: 8px;

      > .-backlight {
        display: none;
      }
      > .-mark {
        left: 0;
        width: 8px;
        height: 8px;
        background-color: var(--mark-colour);
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.3);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
    }
  }

  &.r {
    right: 0;
    > .-backlight {
      top: var(--tm-pxVisualSpanBorder);
      right: var(--tm-pxVisualSpanBorder);
      bottom: var(--tm-pxVisualSpanBorder);
      left: 0;
      background-image: linear-gradient(
        270deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      );
    }
    > .-mark {
      right: 8px;
    }
    &.n {
      right: -8px;
      width: 8px;

      > .-backlight {
        display: none;
      }
      > .-mark {
        right: 0;
        width: 8px;
        height: 8px;
        background-color: var(--mark-colour);
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.3);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
    }
  }
}
</style>
